import { isMultipleChoice } from './questionScoring';
import {
  duplicateMetricIdentifiers,
  isMetricIdentifier,
  REPORT_QUESTION_TYPE,
  type MetricQuestion,
  type QuestionBinding,
  type ReportMetricDefinition,
} from './types';

type ObjectValue = Record<string, any>;
const object = (value: unknown): value is ObjectValue => !!value && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const id = (value: unknown): value is string => typeof value === 'string' && /^[\w-]{1,100}$/.test(value);
const text = (value: unknown): boolean =>
  object(value) && Object.values(value).every((v) => typeof v === 'string' && v.length <= 10000);
const requireValue = (condition: unknown, message: string) => {
  if (!condition) {
    throw new Error(message);
  }
};
const uniqueIds = (items: ObjectValue[]) => {
  requireValue(
    items.every((item) => object(item) && id(item.id)),
    'id',
  );
  requireValue(new Set(items.map((item) => item.id)).size === items.length, 'duplicateId');
};

/**
 * Checks untrusted persisted definition JSON before anything reads it: an object of at most 256 KiB, nesting depth
 * 30, plain JSON values only, no prototype keys, schema version 1. Never coerces or strips fields.
 */
export function assertBoundedJson(value: unknown): void {
  requireValue(object(value), 'definition');
  const document = value as ObjectValue;
  requireValue(new TextEncoder().encode(JSON.stringify(document)).length <= 262144, 'size');
  const walk = (node: unknown, depth: number) => {
    requireValue(depth <= 30, 'depth');
    if (object(node) || Array.isArray(node)) {
      Object.entries(node).forEach(([key, child]) => {
        requireValue(!['__proto__', 'constructor', 'prototype'].includes(key), 'key');
        // Optional object properties are omitted by JSON transport; undefined array entries are not valid.
        if (child !== undefined || Array.isArray(node)) {
          walk(child, depth + 1);
        }
      });
    } else {
      requireValue(node === null || ['string', 'boolean'].includes(typeof node) || finite(node), 'value');
    }
  };
  walk(document, 0);
  requireValue(document.schemaVersion === 1, 'version');
}

/** Semantic validation of the `metrics` array of a definition; throws the first error code. */
export function validateReportMetrics(value: unknown): asserts value is ReportMetricDefinition[] {
  requireValue(Array.isArray(value) && value.length <= 200, 'metrics');
  const metrics = value as ObjectValue[];
  uniqueIds(metrics);
  const metricIds = new Set(metrics.map((metric) => metric.id));
  const binding = (source: ObjectValue) => {
    requireValue(object(source) && id(source.questionIndex), 'question');
    requireValue(
      source.statementIndex === undefined || (Number.isInteger(source.statementIndex) && source.statementIndex >= 0),
      'statement',
    );
  };
  const scoreSource = (source: ObjectValue) => {
    binding(source);
    requireValue(
      Array.isArray(source.optionScores) && source.optionScores.length > 0 && source.optionScores.some(finite),
      'mapping',
    );
    requireValue(finite(source.min) && finite(source.max) && source.max >= source.min, 'range');
    requireValue(
      source.optionScores.every(
        (score: unknown) => score === null || (finite(score) && score >= source.min && score <= source.max),
      ),
      'mapping',
    );
  };
  const scaleSource = (source: ObjectValue) => {
    binding(source);
    requireValue(
      Array.isArray(source.optionScores) && source.optionScores.length > 0 && source.optionScores.some(finite),
      'mapping',
    );
    requireValue(
      source.optionScores.every((score: unknown) => score === null || finite(score)),
      'mapping',
    );
  };
  /** A standard 0–10 (or 1–10) NPS mapping: every score exactly once, extra options excluded. */
  const npsSource = (source: ObjectValue, inputMin: unknown) => {
    scoreSource(source);
    requireValue(inputMin === 0 || inputMin === 1, 'nps');
    const scores = source.optionScores.filter((score: unknown) => score !== null);
    requireValue(
      scores.length === 11 - (inputMin as number) &&
        new Set(scores).size === scores.length &&
        scores.every((score: number) => Number.isInteger(score) && score >= (inputMin as number) && score <= 10),
      'nps',
    );
  };
  /** Scale questions plus optional `scaleTotal` metric references, at least one of either, no duplicates. */
  const scaleSources = (item: ObjectValue) => {
    requireValue(
      item.metricIds === undefined ||
        (Array.isArray(item.metricIds) &&
          new Set(item.metricIds).size === item.metricIds.length &&
          item.metricIds.every(
            (key: unknown) =>
              metricIds.has(key) &&
              metrics.find((metric: ObjectValue) => metric.id === key)?.calculation?.type === 'scaleTotal',
          )),
      'metricReference',
    );
    requireValue(Array.isArray(item.sources) && (item.sources.length > 0 || item.metricIds?.length > 0), 'sources');
    item.sources.forEach(scaleSource);
    requireValue(
      new Set(
        item.sources.map((source: ObjectValue) =>
          JSON.stringify([source.questionIndex, source.statementIndex ?? null]),
        ),
      ).size === item.sources.length,
      'duplicateSource',
    );
  };
  metrics.forEach((metric: ObjectValue) => {
    requireValue(metric.identifier === undefined || isMetricIdentifier(metric.identifier), 'identifier');
    requireValue(text(metric.title) && Object.values(metric.title).some(Boolean) && text(metric.description), 'title');
    const calculation = metric.calculation;
    requireValue(object(calculation), 'calculation');
    switch (calculation.type) {
      case 'average':
      case 'esi':
        requireValue(Array.isArray(calculation.sources) && calculation.sources.length > 0, 'sources');
        calculation.sources.forEach(scoreSource);
        requireValue(
          new Set(
            calculation.sources.map((source: ObjectValue) =>
              JSON.stringify([source.questionIndex, source.statementIndex ?? null]),
            ),
          ).size === calculation.sources.length,
          'duplicateSource',
        );
        requireValue(['none', 'range'].includes(calculation.normalization), 'normalization');
        if (calculation.normalization === 'none') {
          requireValue(
            calculation.sources.every(
              (source: ObjectValue) =>
                source.min === calculation.sources[0].min && source.max === calculation.sources[0].max,
            ),
            'mixedRanges',
          );
        } else {
          requireValue(
            calculation.sources.every((source: ObjectValue) => source.max > source.min),
            'range',
          );
        }
        break;
      case 'nps':
        npsSource(calculation.source, calculation.inputMin);
        break;
      case 'compositeAverage':
        requireValue(
          Array.isArray(calculation.metricIds) &&
            calculation.metricIds.length > 0 &&
            new Set(calculation.metricIds).size === calculation.metricIds.length &&
            calculation.metricIds.every((key: unknown) => metricIds.has(key)),
          'metricReference',
        );
        requireValue(['none', 'range'].includes(calculation.normalization), 'normalization');
        break;
      case 'choiceMatchRate':
        requireValue(
          Array.isArray(calculation.sources) &&
            calculation.sources.length > 0 &&
            typeof calculation.complement === 'boolean',
          'sources',
        );
        calculation.sources.forEach((source: ObjectValue) => {
          binding(source);
          requireValue(
            Array.isArray(source.optionIndexes) &&
              source.optionIndexes.length > 0 &&
              source.optionIndexes.every((index: unknown) => Number.isInteger(index) && Number(index) >= 0),
            'choices',
          );
        });
        break;
      case 'respondentSegment': {
        const conditions = calculation.conditions;
        requireValue(Array.isArray(conditions) && conditions.length > 0 && conditions.length <= 10, 'conditions');
        uniqueIds(conditions);
        conditions.forEach((condition: ObjectValue) => {
          if (condition.kind === 'belowDeviation') {
            requireValue(text(condition.label), 'label');
            scaleSources(condition);
            requireValue(finite(condition.deviations) && condition.deviations > 0, 'deviations');
          } else if (condition.kind === 'npsBucket') {
            npsSource(condition.source, condition.inputMin);
            requireValue(
              Array.isArray(condition.buckets) &&
                condition.buckets.length > 0 &&
                new Set(condition.buckets).size === condition.buckets.length &&
                condition.buckets.every((bucket: unknown) =>
                  ['detractor', 'passive', 'promoter'].includes(bucket as string),
                ),
              'buckets',
            );
          } else {
            throw new Error('conditions');
          }
        });
        requireValue(
          object(calculation.labels) && text(calculation.labels.matching) && text(calculation.labels.other),
          'label',
        );
        break;
      }
      case 'scaleTotal':
      case 'scaleBands':
        scaleSources(calculation);
        if (calculation.type === 'scaleTotal') {
          requireValue(['none', 'range'].includes(calculation.normalization), 'normalization');
        } else {
          const bands = calculation.bands;
          requireValue(Array.isArray(bands) && bands.length >= 2 && bands.length <= 10, 'bands');
          uniqueIds(bands);
          bands.forEach((band: ObjectValue, index: number) => {
            requireValue(text(band.label), 'bands');
            requireValue(finite(band.from), 'bands');
            // Best to worst: every bound must be strictly below the previous one.
            requireValue(index === 0 || band.from < bands[index - 1].from, 'bands');
          });
          requireValue(
            bands.some((band: ObjectValue) => band.id === calculation.primaryBandId),
            'primaryBand',
          );
        }
        break;
      default:
        throw new Error('calculation');
    }
    requireValue(calculation.type !== 'nps' || metric.presentation?.format === 'number', 'presentation');
    requireValue(
      object(metric.presentation) &&
        ['score', 'bar', 'breakdown', 'distribution'].includes(metric.presentation.renderer) &&
        ['outOfMax', 'rangePercent', 'maxPercent', 'number'].includes(metric.presentation.format),
      'presentation',
    );
    // Band and segment shares are already percentages; the distribution view exists only for them.
    const banded = ['scaleBands', 'respondentSegment'].includes(calculation.type);
    requireValue(!banded || ['maxPercent', 'number'].includes(metric.presentation.format), 'presentation');
    requireValue(metric.presentation.renderer !== 'distribution' || banded, 'presentation');
  });
  requireValue(!duplicateMetricIdentifiers(metrics as ReportMetricDefinition[]).length, 'duplicateIdentifier');
  const ranges = new Map<string, [number, number]>();
  const visit = (metricId: string, stack: Set<string>): [number, number] => {
    requireValue(!stack.has(metricId) && stack.size < 20, 'cycle');
    const previous = ranges.get(metricId);
    if (previous) {
      return previous;
    }
    const metric = metrics.find((item: ObjectValue) => item.id === metricId)!;
    const calculation = metric.calculation;
    let range: [number, number] = [0, 100];
    if (calculation.type === 'nps') {
      range = [-100, 100];
    }
    if (calculation.type === 'average' && calculation.normalization === 'none') {
      range = [calculation.sources[0].min, calculation.sources[0].max];
    }
    if (calculation.type === 'scaleTotal') {
      // Scale values start at 1; the maximum is the option count, known only once questions are available.
      range = [1, Number.POSITIVE_INFINITY];
      (calculation.metricIds ?? []).forEach((key: string) => visit(key, new Set([...stack, metricId])));
    }
    if (calculation.type === 'scaleBands') {
      (calculation.metricIds ?? []).forEach((key: string) => visit(key, new Set([...stack, metricId])));
    }
    if (calculation.type === 'respondentSegment') {
      calculation.conditions.forEach((condition: ObjectValue) =>
        (condition.metricIds ?? []).forEach((key: string) => visit(key, new Set([...stack, metricId]))),
      );
    }
    if (calculation.type === 'compositeAverage') {
      const inputs = calculation.metricIds.map((key: string) => visit(key, new Set([...stack, metricId])));
      if (calculation.normalization === 'none') {
        requireValue(
          inputs.every((input: number[]) => input[0] === inputs[0][0] && input[1] === inputs[0][1]),
          'mixedRanges',
        );
        range = inputs[0];
      } else {
        requireValue(
          inputs.every((input: number[]) => input[1] > input[0]),
          'range',
        );
      }
    }
    requireValue(metric.presentation.format !== 'rangePercent' || range[1] > range[0], 'range');
    requireValue(metric.presentation.format !== 'maxPercent' || (range[0] >= 0 && range[1] > 0), 'range');
    ranges.set(metricId, range);
    return range;
  };
  metrics.forEach((metric: ObjectValue) => visit(metric.id, new Set()));
}

/** The validated metrics of a persisted definition; its layout (pages, components, tables) is not checked here. */
export function parseReportDefinitionMetrics(value: unknown): ReportMetricDefinition[] {
  assertBoundedJson(value);
  const { metrics } = value as ObjectValue;
  validateReportMetrics(metrics);
  return metrics;
}

/** Checks the metrics against the report's questions: existing questions/statements, types and option counts. */
export function validateMetricQuestions(
  metrics: readonly ReportMetricDefinition[],
  questions: readonly MetricQuestion[],
): void {
  const check = (source: QuestionBinding) => {
    const question = questions.find((item) => item.index === source.questionIndex);
    requireValue(question, 'question');
    requireValue(
      source.statementIndex === undefined || source.statementIndex < (question!.statementsLocal?.length ?? 0),
      'statement',
    );
    return question!;
  };
  const choiceTypes: string[] = [
    REPORT_QUESTION_TYPE.Select,
    REPORT_QUESTION_TYPE.Multiselect,
    REPORT_QUESTION_TYPE.Scale,
  ];
  metrics.forEach(({ calculation }) => {
    if (calculation.type === 'compositeAverage') {
      return;
    }
    const scaleOnly: QuestionBinding[] =
      calculation.type === 'scaleTotal' || calculation.type === 'scaleBands'
        ? calculation.sources
        : calculation.type === 'respondentSegment'
          ? calculation.conditions.flatMap((condition) =>
              condition.kind === 'belowDeviation' ? condition.sources : [],
            )
          : [];
    const sources: QuestionBinding[] =
      calculation.type === 'nps'
        ? [calculation.source]
        : calculation.type === 'respondentSegment'
          ? [
              ...scaleOnly,
              ...calculation.conditions.flatMap((condition) =>
                condition.kind === 'npsBucket' ? [condition.source] : [],
              ),
            ]
          : calculation.sources;
    scaleOnly.forEach((source) => requireValue(check(source).type === REPORT_QUESTION_TYPE.Scale, 'question'));
    sources.forEach((source) => {
      const question = check(source);
      requireValue(choiceTypes.includes(question.type), 'question');
      if ('optionScores' in source) {
        requireValue(
          !isMultipleChoice(question) &&
            (source as { optionScores: unknown[] }).optionScores.length === question.answersLocal?.length,
          'mapping',
        );
      } else if ('optionIndexes' in source) {
        requireValue(
          (source as { optionIndexes: number[] }).optionIndexes.every(
            (index) => index < (question.answersLocal?.length ?? 0),
          ),
          'choices',
        );
      }
    });
  });
}
